import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';

import { stationsApi } from '../../lib/stationsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible } from '../../lib/libelles';
import { formatHeure, formatNombre } from '../../lib/formats';
import { localBusinessDate } from '../../lib/stationCalc';
import { PageHeader } from '../../components/ui/page-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { CELLULES, Panneau } from '../../components/stations/Panneau';
import { StationPicker, useStations } from '../../components/stations/StationPicker';
import { nomDuPoste } from './ShiftPage';
import { jourLu, veille } from './StationsHomePage';

/**
 * Rapport journalier d'une station (lot 030) : ventes par produit, postes et
 * écarts de caisse, ventes hors espèces, dépenses, cuves. Le PDF reprend le
 * même contenu (`/report.pdf`) ; le résumé WhatsApp part à l'arrêt de la
 * journée. Montants masqués sans « montants visibles ».
 */

const montant = (cache, valeur) => (cache ? 'Masqué' : valeur === null || valeur === undefined ? '—' : formatNombre(valeur, 0));

const signe = (cache, valeur) => {
  if (cache) return 'Masqué';
  const nombre = Number(valeur);
  if (valeur === null || valeur === undefined || !Number.isFinite(nombre)) return '—';
  const texte = formatNombre(Math.abs(nombre), 0);
  return nombre < 0 ? `−${texte}` : texte;
};

const StationReportsPage = () => {
  const navigate = useNavigate();
  const [parametres, setParametres] = useSearchParams();
  const { stations, loading: chargementStations } = useStations();
  const [stationId, setStationId] = useState(parametres.get('station') || '');
  const [date, setDate] = useState(parametres.get('date') || veille(localBusinessDate(new Date())));
  const [rapport, setRapport] = useState(null);
  const [chargement, setChargement] = useState(false);

  const choisirStation = useCallback(
    (id) => {
      setStationId(id);
      setParametres((avant) => {
        const suivant = new URLSearchParams(avant);
        suivant.set('station', id);
        return suivant;
      }, { replace: true });
    },
    [setParametres],
  );

  const charger = useCallback(async (id, jour) => {
    if (!id || !jour) return;
    setChargement(true);
    try {
      setRapport(await stationsApi.reports.daily(id, jour));
    } catch (probleme) {
      setRapport(null);
      toast.error(messageErreur(probleme, 'Impossible de charger le rapport'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger(stationId, date);
  }, [stationId, date, charger]);

  const changerDate = (valeur) => {
    setDate(valeur);
    setParametres((avant) => {
      const suivant = new URLSearchParams(avant);
      suivant.set('date', valeur);
      return suivant;
    }, { replace: true });
  };

  const telechargerPdf = async () => {
    try {
      const fichier = await stationsApi.reports.dailyPdf(stationId, date);
      const adresse = URL.createObjectURL(fichier);
      const lien = document.createElement('a');
      lien.href = adresse;
      lien.download = `rapport-station-${date}.pdf`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setTimeout(() => URL.revokeObjectURL(adresse), 10000);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de préparer le PDF'));
    }
  };

  const cache = Boolean(rapport?.amounts_hidden);
  const station = rapport?.station || stations.find((item) => item.id === stationId);
  const totaux = rapport?.totals || {};
  const postesAuDessus = (rapport?.shifts || []).filter((poste) => poste.cash_exceeds).length;

  if (!chargementStations && stations.length === 0) {
    return (
      <div className="mx-auto w-full max-w-page px-4 py-6">
        <EmptyState
          variant="empty"
          title="Aucune station"
          description="Créez la station, ses cuves et ses pistolets dans les réglages."
          actionLabel="Réglages des stations"
          onAction={() => navigate('/stations/reglages')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Rapport journalier"
        description="Ventes aux index par produit, postes et écarts de caisse, ventes hors espèces, dépenses et cuves d’une journée."
        meta={station ? `${nomLisible(station.name, station.code)}, ${jourLu(date)}` : undefined}
        alertes={[
          rapport?.open_shift_count > 0 && { niveau: 'attention', texte: 'Un poste est encore ouvert : chiffres provisoires', court: 'Poste ouvert' },
          rapport?.status === 'OPEN' && (rapport?.shifts || []).length > 0 && { niveau: 'attention', texte: 'Journée pas encore arrêtée', court: 'À arrêter' },
          postesAuDessus > 0 && {
            niveau: 'critique',
            texte: `${postesAuDessus} poste${postesAuDessus > 1 ? 's' : ''} avec un écart de caisse au-dessus du seuil`,
            court: 'Écart de caisse',
            nombre: postesAuDessus,
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={choisirStation} />
            <Input type="date" aria-label="Journée" value={date} onChange={(event) => changerDate(event.target.value)} className="h-9 w-40" />
            <Button type="button" variant="outline" onClick={telechargerPdf} disabled={!rapport}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              PDF
            </Button>
          </>
        }
      />

      {chargement && !rapport ? (
        <LoadingState shape="table" />
      ) : !rapport ? (
        <EmptyState variant="empty" title="Aucun rapport" description="Choisissez une station et une journée." className="border-0" />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          <Panneau titre="Ventes par produit" droite={<Badge variant={rapport.status === 'OPEN' ? 'warning' : 'success'}>{libelle('station_journee', rapport.status)}</Badge>}>
            {(rapport.products || []).length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucune vente</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Produit</th>
                    <th className="text-right">Litres vendus</th>
                    <th className="text-right">Prix du litre</th>
                    <th className="text-right">Ventes</th>
                    <th className="text-right">Écart cuve</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.products.map((ligne) => (
                    <tr key={ligne.product_id}>
                      <td>{ligne.product_name}</td>
                      <td className="text-right tabular-nums">{formatNombre(ligne.liters, 1)}</td>
                      <td className="text-right tabular-nums">
                        {ligne.unit_price !== null && ligne.unit_price !== undefined ? formatNombre(ligne.unit_price, 0) : 'plusieurs'}
                      </td>
                      <td className="text-right tabular-nums">{montant(cache, ligne.amount)}</td>
                      <td className="text-right tabular-nums">—</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="text-right tabular-nums">{formatNombre(totaux.liters, 1)}</td>
                    <td />
                    <td className="text-right tabular-nums">{montant(cache, totaux.sales_amount)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </Panneau>

          <Panneau titre="Caisse" droite={<span className="text-xs text-muted-foreground">FCFA</span>}>
            <table className={CELLULES}>
              <tbody>
                <tr>
                  <td>Ventes du jour</td>
                  <td className="text-right tabular-nums">{montant(cache, totaux.sales_amount)}</td>
                </tr>
                {(rapport.payments || []).map((ligne) => (
                  <tr key={ligne.method}>
                    <td>{libelle('station_paiement', ligne.method)}</td>
                    <td className="text-right tabular-nums">{montant(cache, ligne.recorded_amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td>Dépenses payées en espèces</td>
                  <td className="text-right tabular-nums">{montant(cache, totaux.cash_expenses)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Espèces attendues</td>
                  <td className="text-right tabular-nums">{montant(cache, totaux.expected_cash)}</td>
                </tr>
                <tr>
                  <td>Espèces comptées</td>
                  <td className="text-right tabular-nums">{montant(cache, totaux.declared_cash)}</td>
                </tr>
                <tr>
                  <td>Écart de caisse</td>
                  <td className="text-right tabular-nums">{signe(cache, totaux.cash_variance)}</td>
                </tr>
              </tfoot>
            </table>
          </Panneau>

          <Panneau titre="Postes" className="xl:col-span-2">
            {(rapport.shifts || []).length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucun poste ce jour</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Poste</th>
                    <th className="text-left">Heures</th>
                    <th className="text-left">Pompistes</th>
                    <th className="text-right">Litres</th>
                    <th className="text-right">Espèces attendues</th>
                    <th className="text-right">Espèces comptées</th>
                    <th className="text-right">Écart</th>
                    <th className="text-left">Justification</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.shifts.map((poste) => (
                    <tr key={poste.id}>
                      <td className="whitespace-nowrap font-medium">
                        {nomDuPoste(poste.label)}
                        {poste.status === 'OPEN' && <Badge variant="warning" className="ml-2">{libelle('station_poste', 'OPEN')}</Badge>}
                      </td>
                      <td className="whitespace-nowrap">
                        {formatHeure(poste.opened_at)}
                        {poste.closed_at ? ` à ${formatHeure(poste.closed_at)}` : ''}
                      </td>
                      <td>{(poste.attendant_names || []).join(', ') || '—'}</td>
                      <td className="text-right tabular-nums">{formatNombre(poste.liters, 1)}</td>
                      <td className="text-right tabular-nums">{montant(cache, poste.expected_cash)}</td>
                      <td className="text-right tabular-nums">{montant(cache, poste.declared_cash)}</td>
                      <td className={`text-right tabular-nums ${poste.cash_exceeds ? 'font-semibold text-destructive' : ''}`}>
                        {signe(cache, poste.cash_variance)}
                      </td>
                      <td className="max-w-xs text-sm">{poste.justification || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panneau>

          <Panneau titre="Dépenses">
            {(rapport.expenses || []).length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucune dépense</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Motif</th>
                    <th className="text-left">Caisse</th>
                    <th className="text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.expenses.map((depense) => (
                    <tr key={depense.id}>
                      <td>
                        {depense.category}
                        {depense.description ? <span className="block text-xs text-muted-foreground">{depense.description}</span> : null}
                      </td>
                      <td>{depense.paid_in_cash ? 'Espèces' : 'Hors caisse'}</td>
                      <td className="text-right tabular-nums">{montant(cache, depense.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panneau>

          <Panneau titre="Cuves" droite={<span className="text-xs text-muted-foreground">Litres</span>}>
            {(rapport.tanks || []).length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucune cuve</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Cuve</th>
                    <th className="text-left">Produit</th>
                    <th className="text-right">Stock</th>
                    <th className="text-right">Capacité</th>
                    <th className="text-left">Seuil</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.tanks.map((cuve) => (
                    <tr key={cuve.id}>
                      <td>{cuve.name}</td>
                      <td>{cuve.product_name || '—'}</td>
                      <td className="text-right tabular-nums">{formatNombre(cuve.current_volume_liters, 0)}</td>
                      <td className="text-right tabular-nums">{formatNombre(cuve.capacity_liters, 0)}</td>
                      <td>{cuve.below_threshold ? <Badge variant="destructive">Sous le seuil</Badge> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panneau>

          {rapport.lock_justification && (
            <Panneau titre="Justification de l’arrêt" className="xl:col-span-2">
              <p className="px-3 text-sm">{rapport.lock_justification}</p>
            </Panneau>
          )}
        </div>
      )}
    </div>
  );
};

export default StationReportsPage;
